import React, { useState } from 'react';
import { Button, Card, Table, Badge, Spinner } from 'flowbite-react';
import { useGetAssignedTickets } from 'src/hooks/supportTicket.hooks';
import { SupportTicketStatus } from 'types';
import { useNavigate } from 'react-router-dom';
import Container from 'src/components/shared/Container';

const AMAssignedTickets = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<string>('');

    const { data: ticketsData, isLoading } = useGetAssignedTickets(statusFilter);
    const tickets = ticketsData?.data || [];

    const getStatusBadge = (status: SupportTicketStatus | string) => {
        const colors: Record<SupportTicketStatus, string> = {
            [SupportTicketStatus.OPEN]: 'info',
            [SupportTicketStatus.RESOLVED]: 'success',
        };
        return colors[status as SupportTicketStatus] ?? (status === 'in_progress' || status === 'escalated' ? 'info' : status === 'closed' ? 'success' : 'gray');
    };

    const getStatusLabel = (status: string) => (status === 'in_progress' || status === 'escalated' ? 'Open' : status === 'closed' ? 'Resolved' : (status?.charAt(0)?.toUpperCase() ?? '') + (status?.slice(1) ?? ''));

    const getCategoryBadge = (category: string) => {
        const colors: Record<string, string> = {
            normal: 'info',
            position: 'warning',
            company: 'purple',
        };
        return colors[category] || 'gray';
    };

    return (
        <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
            <div className="mb-6">
                <h1 className="text-2xl font-bold dark:text-white">My Assigned Tickets</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Tickets assigned to you for resolution
                </p>
            </div>

            <Card className="bg-white dark:bg-[#1F2A37]">
                {/* Filter Tabs */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            color={statusFilter === '' ? 'purplePlaced' : 'gray'}
                            onClick={() => setStatusFilter('')}
                        >
                            All ({tickets.length})
                        </Button>
                        <Button
                            size="sm"
                            color={statusFilter === SupportTicketStatus.OPEN ? 'purplePlaced' : 'gray'}
                            onClick={() => setStatusFilter(SupportTicketStatus.OPEN)}
                        >
                            Unresolved ({tickets.filter((t: any) => t.status === SupportTicketStatus.OPEN || t.status === 'in_progress' || t.status === 'escalated').length})
                        </Button>
                        <Button
                            size="sm"
                            color={statusFilter === SupportTicketStatus.RESOLVED ? 'purplePlaced' : 'gray'}
                            onClick={() => setStatusFilter(SupportTicketStatus.RESOLVED)}
                        >
                            Resolved ({tickets.filter((t: any) => t.status === SupportTicketStatus.RESOLVED).length})
                        </Button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <Spinner size="xl" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No tickets assigned to you.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <Table.Head>
                                <Table.HeadCell>Ticket ID</Table.HeadCell>
                                <Table.HeadCell>Subject</Table.HeadCell>
                                <Table.HeadCell>Student</Table.HeadCell>
                                <Table.HeadCell>Category</Table.HeadCell>
                                <Table.HeadCell>Status</Table.HeadCell>
                                <Table.HeadCell>Created</Table.HeadCell>
                                <Table.HeadCell>Unread Messages</Table.HeadCell>
                                <Table.HeadCell>
                                    <span className="sr-only">Action</span>
                                </Table.HeadCell>
                            </Table.Head>
                            <Table.Body className="divide-y">
                                {tickets.map((ticket: any) => {
                                    const unreadCount = ticket.messages?.filter(
                                        (msg: any) => !msg.isRead && msg.senderType === 'user',
                                    ).length || 0;

                                    return (
                                        <Table.Row
                                            key={ticket._id}
                                            className="bg-white dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                            onClick={() => navigate(`/tickets/${ticket._id}`)}
                                        >
                                            <Table.Cell className="font-mono text-sm">{ticket._id}</Table.Cell>
                                            <Table.Cell className="max-w-xs">
                                                <div className="truncate">{ticket.subject}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div>
                                                    <div className="font-medium">{ticket.user?.name || 'N/A'}</div>
                                                    <div className="text-sm text-gray-500">{ticket.user?.email || ''}</div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getCategoryBadge(typeof ticket.category === 'object' ? ticket.category?.label : ticket.category)}>
                                                    {typeof ticket.category === 'object' && ticket.category !== null
                                                        ? (ticket.category?.label || ticket.category?.name || '')
                                                        : (ticket.category ?? '')}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getStatusBadge(ticket.status)}>{getStatusLabel(ticket.status)}</Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {new Date(ticket.createdAt).toLocaleDateString()}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {unreadCount > 0 ? (
                                                    <Badge color="failure" className="w-fit">
                                                        {unreadCount}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Button
                                                    size="sm"
                                                    color="purplePlaced"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/tickets/${ticket._id}`);
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </div>
                )}
            </Card>
        </Container>
    );
};

export default AMAssignedTickets;

